// your-select.jsx
import React, {
  useState,
  useRef,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import "./styles.css";
import {
  Root as SelectRoot,
  Trigger as SelectTrigger,
  Value as SelectValue,
  Icon as SelectIcon,
  Portal as SelectPortal,
  Content as SelectContent,
  Viewport as SelectViewport,
  Item as SelectItemPrimitive,
  ItemText as SelectItemText,
  Label as SelectLabel,
  Group as SelectGroup,
} from "@radix-ui/react-select";
import ChevronDown from "../icons/ChevronDown";
import classnames from "classnames";

const Select = ({
  options,
  placeholder,
  groupLabel,
  onChange,
  disabled,
  value,
}) => {
  const [internalValue, setInternalValue] = useState(value);

  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  function handleChange(...args) {
    setInternalValue(...args);
  }

  useEffect(() => {
    if (internalValue) onChange(internalValue);
  }, [internalValue]);

  useEffect(() => {
    if (internalValue !== value) {
      setInternalValue(value);
    }
  }, [value]);

  // This runs after layout and ensures the width is set *after* render
  useLayoutEffect(() => {
    const observer = new ResizeObserver(() => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    });

    if (triggerRef.current) {
      observer.observe(triggerRef.current);
      setTriggerWidth(triggerRef.current.offsetWidth);
    }

    return () => observer.disconnect();
  }, []);

  const itemOptions = options.map((option) => (
    <SelectItemPrimitive
      key={option.value}
      value={option.value}
      className={classnames(
        "hover:bg-violet-600/20 hover:text-stone-900 rounded-[3px] px-[5px] py-[7px] mx-[10px] flex text-stone-300 text-xs font-medium font-custom items-center bg-accent cursor-pointer border-none outline-none",
        option.value === internalValue && "bg-violet-600/20 text-stone-900"
      )}
    >
      <SelectItemText>{option.label}</SelectItemText>
    </SelectItemPrimitive>
  ));

  return (
    <SelectRoot
      value={internalValue}
      onValueChange={handleChange}
      disabled={disabled}
    >
      <SelectTrigger
        ref={triggerRef}
        className={classnames(
          "w-full py-3 px-4  text-xs font-medium font-custom flex items-center justify-between bg-white rounded-[10px] border border-neutral-200/60",
          !!internalValue ? "text-stone-900" : "text-stone-300"
        )}
      >
        <SelectValue placeholder={placeholder} />
        <SelectIcon className="ml-2">
          <ChevronDown />
        </SelectIcon>
      </SelectTrigger>

      <SelectPortal>
        <SelectContent
          position="popper"
          side="bottom"
          sideOffset={10}
          style={{ width: triggerWidth }}
          className="rounded-[10px] bg-white shadow overflow-hidden py-3"
        >
          <SelectViewport>
            <SelectGroup className="space-y-1">
              {groupLabel && (
                <SelectLabel className="text-stone-900 text-xs font-semibold font-custom px-[14px] mb-[5px]">
                  {groupLabel}
                </SelectLabel>
              )}

              {itemOptions}
            </SelectGroup>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  );
};

export default Select;
